import React, { useState, useEffect } from 'react';
import {
    Grid,
    Button,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    DialogContent
} from '@mui/material';
import { Add } from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import RootDialogPage from '../../Commun/components/RootDialogPage';
import { fuelApi, FuelTransaction } from '../services/fuelApi';
import { vehiclesApi, Vehicle } from '../services/vehiclesApi';
import { driversApi, Driver } from '../services/driversApi';
import { fuelCardsApi, FuelCard } from '../services/fuelCardsApi';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';

const Fuel: React.FC = () => {
    const { t } = useLanguage();
    
    
    const [transactions, setTransactions] = useState<FuelTransaction[]>([]);
    const [vehicles, setVehicles] = useState<Vehicle[]>([]);
    const [drivers, setDrivers] = useState<Driver[]>([]);
    const [cards, setCards] = useState<FuelCard[]>([]);
    
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingTx, setEditingTx] = useState<FuelTransaction | null>(null);
    const formatDateForInput = (d: any) => {
        if (!d) return '';
        const date = new Date(d);
        return isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
    };

    const [formData, setFormData] = useState<Partial<FuelTransaction>>({
        vehicleId: undefined,
        fuelCardId: undefined,
        driverId: undefined,
        date: new Date(),
        liters: 0,
        cost: 0,
        odometer: 0,
        station: '',
        product: '',
        pricePerLiter: 0
    });

    const loadData = async () => {
        try {
            const [txRes, vehRes, drvRes, cardRes] = await Promise.all([
                fuelApi.getAll(),
                vehiclesApi.getAll(),
                driversApi.getAll(),
                fuelCardsApi.getAll()
            ]);
            
            if (txRes.success) setTransactions(txRes.data);
            if (vehRes.success) setVehicles(vehRes.data);
            if (drvRes.success) setDrivers(drvRes.data);
            if (cardRes.success) setCards(cardRes.data);
        } catch (error) {
            console.error('Failed to load data:', error);
            blobticMessage.error(t('common.messages.error'), 'Failed to load fuel data');
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOpenDialog = (tx?: FuelTransaction) => {
        if (tx) {
            setEditingTx(tx);
            setFormData({ ...tx });
        } else {
            setEditingTx(null);
            setFormData({
                vehicleId: vehicles.length > 0 ? vehicles[0].id : undefined,
                fuelCardId: cards.length > 0 ? cards[0].id : undefined,
                driverId: drivers.length > 0 ? drivers[0].id : undefined,
                date: new Date(),
                liters: 0,
                cost: 0,
                odometer: 0,
                station: '',
                product: 'Diesel',
                pricePerLiter: 130
            });
        }
        setIsDialogOpen(true);
    };

    const handleSave = async () => {
        if (!formData.vehicleId || !formData.liters || !formData.cost) {
            blobticMessage.error(t('common.messages.warning') || 'Warning', t('zoom.check.fillRequired'));
            return;
        }

        try {
            let response;
            if (editingTx) {
                response = await fuelApi.update(editingTx.id, formData);
            } else {
                response = await fuelApi.create(formData);
            }

            if (response.success) {
                blobticMessage.success(t('common.messages.success'), t('common.messages.saved'));
                setIsDialogOpen(false);
                loadData();
            } else {
                blobticMessage.error(t('common.messages.error') || 'Error', response.message || 'Unknown error');
            }
        } catch (error) {
            console.error('Error saving transaction:', error);
            blobticMessage.error(t('common.messages.error'), t('common.messages.error'));
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm(t('common.confirm.delete') || 'Are you sure?')) return;
        try {
            const response = await fuelApi.delete(id);
            if (response.success) {
                blobticMessage.success(t('common.messages.success'), t('common.messages.deleted_success'));
                loadData();
            } else {
                blobticMessage.error(t('common.messages.error') || 'Error', response.message || 'Unknown error');
            }
        } catch (error) {
            blobticMessage.error(t('common.messages.error') || 'Error', t('common.messages.delete_error'));
        }
    };

    const columns = [
        {
            id: 'date',
            label: t('parc.repairs.date') || 'Date',
            minWidth: 120,
            format: (value: Date) => value.toLocaleDateString()
        },
        { id: 'vehicleRegistration', label: t('parc.gpsTracking.vehicle') || 'Véhicule', minWidth: 120 },
        { id: 'driverName', label: t('parc.trips.driver') || 'Conducteur', minWidth: 150 },
        { id: 'fuelCardNumber', label: t('parc.fuel.fuelCard') || 'Carte', minWidth: 120 },
        { id: 'product', label: t('parc.fuel.product') || 'Produit', minWidth: 100 },
        { id: 'liters', label: t('parc.fuel.liters') || 'Litres', minWidth: 100, format: (val: number) => `${val.toFixed(2)} L` },
        { id: 'pricePerLiter', label: t('parc.fuel.pricePerLiter') || 'Prix/Litre', minWidth: 100, format: (val: number) => `${val} DZD` },
        { id: 'cost', label: t('parc.fuel.cost') || 'Coût', minWidth: 100, format: (val: number) => `${val.toFixed(2)} DZD` },
        { id: 'odometer', label: t('parc.fuel.odometer') || 'Kilométrage', minWidth: 120, format: (val: number) => `${val} km` },
        { id: 'station', label: t('parc.fuel.station') || 'Station', minWidth: 150 }
    ];

    const additionalActions = (
        <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
        >
            {t('parc.fuel.newTransaction')}
        </Button>
    );

    return (
        <GenericPage title={t('menu.parc.fuel') || 'Carburant'} additionalActions={additionalActions} footerEnabled={false} disableScroll>
            <GenericTable
                columns={columns}
                data={transactions}
                onEdit={handleOpenDialog}
                onDelete={(v: any) => handleDelete(v.id)}
            />

            <RootDialogPage
                open={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                title={editingTx ? t('parc.fuel.editTransaction') : t('parc.fuel.newTransaction')}
                onAction={handleSave}
            >
                <DialogContent>
                    <Grid container spacing={2} sx={{ pt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required>
                                <InputLabel>{t('parc.gpsTracking.vehicle')}</InputLabel>
                                <Select
                                    value={formData.vehicleId || ''}
                                    label={t('parc.gpsTracking.vehicle')}
                                    onChange={(e) => setFormData({ ...formData, vehicleId: Number(e.target.value) })}
                                >
                                    {vehicles.map((v) => (
                                        <MenuItem key={v.id} value={v.id}>
                                            {v.registrationNumber} ({v.brand} {v.model})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>{t('parc.trips.driver')}</InputLabel>
                                <Select
                                    value={formData.driverId || ''}
                                    label={t('parc.trips.driver')}
                                    onChange={(e) => setFormData({ ...formData, driverId: Number(e.target.value) })}
                                >
                                    {drivers.map((d) => (
                                        <MenuItem key={d.id} value={d.id}>
                                            {d.firstName} {d.lastName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>{t('parc.fuel.fuelCard')}</InputLabel>
                                <Select
                                    value={formData.fuelCardId || ''}
                                    label={t('parc.fuel.fuelCard')}
                                    onChange={(e) => setFormData({ ...formData, fuelCardId: Number(e.target.value) })}
                                >
                                    <MenuItem value=""><em>{t('parc.fuel.none')}</em></MenuItem>
                                    {cards.map((c) => (
                                        <MenuItem key={c.id} value={c.id}>
                                            {c.cardNumber}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="date"
                                label={t('parc.repairs.date')}
                                InputLabelProps={{ shrink: true }}
                                value={formatDateForInput(formData.date)}
                                onChange={(e) => setFormData({ ...formData, date: new Date(e.target.value) })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                type="number"
                                label={t('parc.fuel.liters')}
                                value={formData.liters || ''}
                                onChange={(e) => {
                                    const liters = Number(e.target.value);
                                    const price = formData.pricePerLiter || 0;
                                    setFormData({
                                        ...formData,
                                        liters,
                                        cost: Number((liters * price).toFixed(2))
                                    });
                                }}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                type="number"
                                label={t('parc.fuel.pricePerLiter')}
                                value={formData.pricePerLiter || ''}
                                onChange={(e) => {
                                    const price = Number(e.target.value);
                                    const liters = formData.liters || 0;
                                    setFormData({
                                        ...formData,
                                        pricePerLiter: price,
                                        cost: Number((liters * price).toFixed(2))
                                    });
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                type="number"
                                label={t('parc.fuel.cost')}
                                value={formData.cost || ''}
                                onChange={(e) => setFormData({ ...formData, cost: Number(e.target.value) })}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label={t('parc.fuel.odometer')}
                                value={formData.odometer || ''}
                                onChange={(e) => setFormData({ ...formData, odometer: Number(e.target.value) })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t('parc.fuel.station')}
                                value={formData.station || ''}
                                onChange={(e) => setFormData({ ...formData, station: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={t('parc.fuel.product')}
                                value={formData.product || ''}
                                onChange={(e) => setFormData({ ...formData, product: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
            </RootDialogPage>
        </GenericPage>
    );
};

export default Fuel;
